import type { components } from '../../../api/generated/schema'
import { disciplinesOf, specializationDiscipline, specializationElement } from '../types'
import { Block, Bubble, Row } from './PrintBlocks'
import { CAPACITY, figure, padRows, signed } from './printModel'
import styles from './print.module.css'

type Schema = components['schemas']

/**
 * The spellcasting page — the printed equivalent of the official Spellcasting Sheet, and the
 * second and last page a character ever gets.
 *
 * It prints only when the server says the character casts (`core.hasSpellcasting`), which is
 * the same flag the screen sheet's magic cards hang off: the spellcasting template, or any
 * ability that grants a discipline.
 *
 * **Nothing on this page is computed here.** Specialization budgets, the shared magic-item
 * pool, an enchanted item's power and its cap, a rote spell's maximum shifts and its stress —
 * all of it arrives derived from the API (architecture §4.4). The Spell Totals box prints
 * blank for the same reason it is blank on paper: those totals are worked out at the table,
 * mid-scene, from whatever the wizard is holding at that moment.
 */
export function PrintSpellcasting({
  skills,
  specializations,
  focusItems,
  enchantedItems,
  roteSpells,
  spellcasting,
}: {
  skills: Schema['SkillsSectionDto'] | undefined
  specializations: Schema['SpecializationsSectionDto'] | undefined
  focusItems: Schema['FocusItemsSectionDto'] | undefined
  enchantedItems: Schema['EnchantedItemsSectionDto'] | undefined
  roteSpells: Schema['RoteSpellsSectionDto'] | undefined
  spellcasting: Schema['SpellcastingType'] | undefined
}) {
  return (
    <div className={styles.columns}>
      <div className={styles.column}>
        <CoreSkillsBlock skills={skills} />
        <SpecializationsBlock
          title="Evocation Specializations"
          discipline="Evocation"
          columns={['Element', 'Control', 'Power']}
          specializations={specializations}
          spellcasting={spellcasting}
        />
        <SpecializationsBlock
          title="Thaumaturgy Specializations"
          discipline="Thaumaturgy"
          columns={['Application', 'Control', 'Complexity']}
          specializations={specializations}
          spellcasting={spellcasting}
        />
        <WorksheetBlock />
      </div>

      <div className={styles.column}>
        <FocusItemsBlock focusItems={focusItems} />
        <EnchantedItemsBlock enchantedItems={enchantedItems} />
        <RoteSpellsBlock roteSpells={roteSpells} />
      </div>
    </div>
  )
}

/** The three skills every spellcasting number is built from, and what each one buys. */
const CORE_SKILLS = [
  { name: 'Conviction', use: 'Spell power of 1-stress casting' },
  { name: 'Discipline', use: 'Control a spell · aim a spell' },
  { name: 'Lore', use: '# rotes · “no-prep” complexity' },
]

function CoreSkillsBlock({ skills }: { skills: Schema['SkillsSectionDto'] | undefined }) {
  const entries = skills?.skills ?? []

  return (
    <Block title="Core Skills">
      <div className={styles.coreSkills}>
        {CORE_SKILLS.map((core) => {
          const entry = entries.find((skill) => skill.name === core.name)

          return (
            <div key={core.name} className={styles.coreSkill}>
              <Bubble value={entry === undefined ? '' : signed(entry.levelValue ?? 0)} large />
              <span className={styles.coreSkillName}>{core.name}</span>
              <span className={styles.coreSkillUse}>{core.use}</span>
            </div>
          )
        })}
      </div>
    </Block>
  )
}

function SpecializationsBlock({
  title,
  discipline,
  columns,
  specializations,
  spellcasting,
}: {
  title: string
  discipline: 'Evocation' | 'Thaumaturgy'
  /** The official sheet's column heads, which differ between the two disciplines. */
  columns: [string, string, string]
  specializations: Schema['SpecializationsSectionDto'] | undefined
  spellcasting: Schema['SpellcastingType'] | undefined
}) {
  const budget =
    discipline === 'Evocation' ? specializations?.evocation : specializations?.thaumaturgy
  const entries = (specializations?.specializations ?? []).filter(
    (entry) => specializationDiscipline(entry) === discipline,
  )
  const granted = disciplinesOf(spellcasting).includes(discipline)

  return (
    <Block title={title}>
      <div className={styles.colHeads}>
        <span className={styles.rowMain}>{columns[0]}</span>
        <span className={styles.rowTrail}>
          {columns[1]} · {columns[2]}
        </span>
      </div>

      {padRows(entries, CAPACITY.specializations).map((entry, index) => (
        <Row
          key={entry?.id ?? `blank-${index}`}
          {...(entry === null ? {} : { trail: `+${entry.control ?? 0} · +${entry.power ?? 0}` })}
        >
          {entry !== null && <span className={styles.rowName}>{specializationElement(entry)}</span>}
        </Row>
      ))}

      <div className={styles.slotsAvailable}>
        <Bubble value={figure(budget?.available)} />
        <span className={styles.slotsAvailableLabel}>
          Slots available
          {!granted && <> · no ability grants {discipline.toLowerCase()}</>}
        </span>
      </div>
    </Block>
  )
}

/** The write-in half of the paper sheet. Blank by design — see the module comment. */
const WORKSHEET_ROWS = [
  'Offensive control (base = Discipline)',
  'Offensive power (base = Conviction)',
  'Defensive control (base = Discipline)',
  'Defensive power (base = Conviction)',
]

function WorksheetBlock() {
  return (
    <Block title="Spell Totals Workspace" grow>
      {WORKSHEET_ROWS.map((row) => (
        <div key={row} className={`${styles.footNote} ${styles.worksheetRow}`}>
          <span>{row}</span>
        </div>
      ))}
    </Block>
  )
}

function FocusItemsBlock({
  focusItems,
}: {
  focusItems: Schema['FocusItemsSectionDto'] | undefined
}) {
  const items = focusItems?.items ?? []

  return (
    <Block title="Focus Items">
      <div className={styles.colHeads}>
        <span className={styles.rowMain}>Description</span>
        <span className={styles.rowTrail}>Bonuses</span>
      </div>

      {padRows(items, CAPACITY.focusItems).map((item, index) => (
        <Row
          key={item?.id ?? `blank-${index}`}
          {...(item === null
            ? {}
            : {
                trail: [
                  String(item.element ?? ''),
                  (item.controlBonus ?? 0) > 0 ? `control +${item.controlBonus}` : '',
                  (item.powerBonus ?? 0) > 0 ? `power +${item.powerBonus}` : '',
                ]
                  .filter((tag) => tag !== '' && tag !== 'None')
                  .join(' · '),
              })}
        >
          {item !== null && <span className={styles.rowName}>{item.name}</span>}
        </Row>
      ))}

      <div className={styles.slotsAvailable}>
        <Bubble value={figure(focusItems?.budget?.remaining)} />
        <span className={styles.slotsAvailableLabel}>
          Slots available
          <br />
          (shared with enchanted items)
        </span>
      </div>
    </Block>
  )
}

function EnchantedItemsBlock({
  enchantedItems,
}: {
  enchantedItems: Schema['EnchantedItemsSectionDto'] | undefined
}) {
  const items = enchantedItems?.items ?? []

  return (
    <Block title="Enchanted Items &amp; Potions">
      <div className={styles.colHeads}>
        <span className={styles.rowMain}>Description</span>
        <span className={styles.rowTrail}>Effects · uses</span>
      </div>

      {padRows(items, CAPACITY.enchantedItems).map((item, index) => (
        <Row
          key={item?.id ?? `blank-${index}`}
          {...(item === null
            ? {}
            : {
                trail: [
                  `power ${item.power ?? 0}`,
                  (item.weapon ?? '') !== '' ? `weapon ${item.weapon}` : '',
                  (item.armor ?? '') !== '' ? `armour ${item.armor}` : '',
                  `${item.currentUses ?? 0}/${item.maxUses ?? 0}`,
                ]
                  .filter((tag) => tag !== '')
                  .join(' · '),
              })}
        >
          {item !== null && <span className={styles.rowName}>{item.name}</span>}
        </Row>
      ))}

      <div className={styles.slotsAvailable}>
        <Bubble value={figure(enchantedItems?.budget?.remaining)} />
        <span className={styles.slotsAvailableLabel}>Slots available</span>
      </div>
    </Block>
  )
}

function RoteSpellsBlock({
  roteSpells,
}: {
  roteSpells: Schema['RoteSpellsSectionDto'] | undefined
}) {
  const spells = roteSpells?.spells ?? []

  return (
    <Block title="Rote Spells" grow>
      <div className={styles.colHeads}>
        <span className={styles.rowCost}>Shifts</span>
        <span className={styles.rowMain}>Spell</span>
        <span className={styles.rowTrail}>Stress</span>
      </div>

      {padRows(spells, CAPACITY.roteSpells).map((spell, index) => (
        <Row
          key={spell?.id ?? `blank-${index}`}
          cost={spell === null ? '' : `${spell.shifts ?? 0}/${spell.maxShifts ?? 0}`}
          {...(spell === null
            ? {}
            : { trail: `${String(spell.element ?? '')} · ${spell.stress ?? 0}` })}
        >
          {spell !== null && <span className={styles.rowName}>{spell.name}</span>}
        </Row>
      ))}

      <div className={styles.slotsAvailable}>
        <Bubble value={figure(roteSpells?.limit)} />
        <span className={styles.slotsAvailableLabel}>
          Rotes available
          <br />
          (= Lore rating)
        </span>
      </div>
    </Block>
  )
}
